import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Minus, Plus, X, Check, Lock, MessageCircle, Smartphone, CreditCard, Banknote } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { formatPrice, cartTotal } from "../currency";
import { useRazorpay } from "../useRazorpay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LP = {
    en: { payOnline: "Pay by Card · Stripe", phoneOrder: "Order by WhatsApp", razorpay: "Pay with UPI / Cards · Razorpay", pin: "PIN Code", phPin: "6-digit PIN", pinErr: "Please enter a valid 6-digit PIN code.", email: "Email (required, for order updates)", emailErr: "Please enter a valid email so we can send your order confirmation.",
        method: "Payment method", upi: "UPI / Cards", upiSub: "Razorpay · GPay, PhonePe, cards", card: "International Card", cardSub: "Stripe · Visa, Mastercard", cod: "Cash on Delivery", codSub: "Pay when your parcel arrives",
        subtotal: "Subtotal", delivery: "Delivery", deliveryFree: "Confirmed on WhatsApp", deliveryPin: "Enter PIN for rate", deliveryVia: "via", free: "Free", pay: "Pay", placeCod: "Place COD order", quoting: "Checking rates…" },
    fr: { payOnline: "Payer par Carte · Stripe", phoneOrder: "Commander sur WhatsApp", razorpay: "Payer UPI / Carte · Razorpay", pin: "Code postal (Inde)", phPin: "PIN à 6 chiffres", pinErr: "Veuillez saisir un code PIN valide à 6 chiffres.", email: "E-mail (requis, pour le suivi)", emailErr: "Veuillez saisir un e-mail valide pour recevoir votre confirmation.",
        method: "Mode de paiement", upi: "UPI / Cartes", upiSub: "Razorpay · GPay, PhonePe, cartes", card: "Carte internationale", cardSub: "Stripe · Visa, Mastercard", cod: "Paiement à la livraison", codSub: "Payez à la réception du colis",
        subtotal: "Sous-total", delivery: "Livraison", deliveryFree: "Confirmée sur WhatsApp", deliveryPin: "Saisir le PIN", deliveryVia: "via", free: "Offerte", pay: "Payer", placeCod: "Commander (paiement à la livraison)", quoting: "Vérification des tarifs…" },
    ar: { payOnline: "ادفع بالبطاقة · Stripe", phoneOrder: "اطلب عبر واتساب", razorpay: "ادفع UPI / بطاقة · Razorpay", pin: "الرمز البريدي (الهند)", phPin: "6 أرقام", pinErr: "يرجى إدخال رمز بريدي صحيح من 6 أرقام.", email: "البريد الإلكتروني (مطلوب للتحديثات)", emailErr: "يرجى إدخال بريد إلكتروني صحيح لإرسال تأكيد طلبك.",
        method: "طريقة الدفع", upi: "UPI / بطاقات", upiSub: "Razorpay · GPay، PhonePe، بطاقات", card: "بطاقة دولية", cardSub: "Stripe · Visa، Mastercard", cod: "الدفع عند الاستلام", codSub: "ادفع عند وصول الطرد",
        subtotal: "المجموع الفرعي", delivery: "التوصيل", deliveryFree: "يُؤكد عبر واتساب", deliveryPin: "أدخل الرمز البريدي", deliveryVia: "عبر", free: "مجاني", pay: "ادفع", placeCod: "أكّد الطلب (الدفع عند الاستلام)", quoting: "جارٍ التحقق من الأسعار…" },
    bn: { payOnline: "কার্ডে পেমেন্ট · Stripe", phoneOrder: "হোয়াটসঅ্যাপে অর্ডার করুন", razorpay: "UPI / কার্ডে পেমেন্ট · Razorpay", pin: "পিন কোড (ভারত)", phPin: "৬ সংখ্যার পিন", pinErr: "অনুগ্রহ করে সঠিক ৬ সংখ্যার পিন কোড দিন।", email: "ইমেইল (আবশ্যক, অর্ডার আপডেটের জন্য)", emailErr: "অর্ডার নিশ্চিতকরণ পাঠাতে একটি সঠিক ইমেইল দিন।",
        method: "পেমেন্ট পদ্ধতি", upi: "UPI / কার্ড", upiSub: "Razorpay · GPay, PhonePe, কার্ড", card: "আন্তর্জাতিক কার্ড", cardSub: "Stripe · Visa, Mastercard", cod: "ক্যাশ অন ডেলিভারি", codSub: "পার্সেল পৌঁছালে পেমেন্ট করুন",
        subtotal: "সাবটোটাল", delivery: "ডেলিভারি", deliveryFree: "হোয়াটসঅ্যাপে নিশ্চিত হবে", deliveryPin: "রেটের জন্য পিন দিন", deliveryVia: "মাধ্যমে", free: "ফ্রি", pay: "পেমেন্ট করুন", placeCod: "COD অর্ডার করুন", quoting: "রেট দেখা হচ্ছে…" },
};

const Field = ({ label, ...props }) => (
    <label className="block">
        <span className="text-[10px] tracking-[0.25em] uppercase text-ash block mb-1">{label}</span>
        <input
            {...props}
            className="w-full bg-transparent border-b border-charcoal/20 focus:border-gold outline-none py-2 text-sm text-charcoal transition-colors duration-300"
        />
    </label>
);

export const CartDrawer = ({ open, onClose, cart, onQty, onRemove, onClear }) => {
    const { t, lang } = useLang();
    const lp = LP[lang] ?? LP.en;
    const [form, setForm] = useState({ name: "", phone: "", address: "", email: "", pincode: "", city: "", state: "" });
    const [placing, setPlacing] = useState(false);
    const [paying, setPaying] = useState(false);
    const [placed, setPlaced] = useState(false);
    const [rzpPaying, setRzpPaying] = useState(false);
    const razorpay = useRazorpay();
    const [method, setMethod] = useState("razorpay");
    const [quote, setQuote] = useState(null);
    const [quoting, setQuoting] = useState(false);
    const deliveryRef = useRef(null);
    const total = cart.reduce((s, i) => s + i.price * i.qty, 0);
    const pinRequired = lang === "en";
    const activeMethod = method === "razorpay" && !razorpay.enabled ? "stripe" : method;
    const fee = quote?.available ? (activeMethod === "cod" ? quote.cod : quote.prepaid) : 0;
    const grand = total + fee;
    const itemsKey = cart.map((i) => `${i.id}:${i.qty}`).join(",");

    useEffect(() => {
        if (form.pincode.length !== 6 || !itemsKey) {
            setQuote(null);
            return;
        }
        let live = true;
        setQuoting(true);
        axios.get(`${API}/shipping/quote`, { params: { pincode: form.pincode, items: itemsKey } })
            .then(({ data }) => live && setQuote(data))
            .catch(() => live && setQuote(null))
            .finally(() => live && setQuoting(false));
        return () => { live = false; };
    }, [form.pincode, itemsKey]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const setPin = (e) => {
        const pincode = e.target.value.replace(/\D/g, "").slice(0, 6);
        setForm((f) => ({ ...f, pincode, city: "", state: "" }));
        if (pincode.length === 6) {
            axios.get(`${API}/pincode/${pincode}`)
                .then(({ data }) => setForm((f) => (f.pincode === pincode ? { ...f, city: data.city, state: data.state } : f)))
                .catch(() => {});
        }
    };
    const payload = () => ({
        name: form.name, phone: form.phone, address: form.address, email: form.email.trim(),
        pincode: form.pincode.length === 6 ? form.pincode : null, city: form.city || null, state: form.state || null,
    });
    const formValid = () => {
        if (!form.name.trim() || !form.phone.trim() || !form.address.trim()) {
            deliveryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            toast.error(t("cart.errorFill"));
            return false;
        }
        const email = form.email.trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            deliveryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            toast.error(lp.emailErr);
            return false;
        }
        if (pinRequired && form.pincode.length !== 6) {
            deliveryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            toast.error(lp.pinErr);
            return false;
        }
        return true;
    };

    const payOnline = async () => {
        if (!formValid()) return;
        setPaying(true);
        try {
            const { data } = await axios.post(`${API}/payments/checkout`, {
                ...payload(),
                items: cart.map((i) => ({ product_id: i.id, qty: i.qty })),
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch {
            toast.error(t("cart.errorFail"));
            setPaying(false);
        }
    };

    const payRazorpay = async () => {
        if (!formValid()) return;
        setRzpPaying(true);
        try {
            await razorpay.pay({
                form: payload(),
                cart,
                onDismiss: () => setRzpPaying(false),
                onSuccess: (orderId) => {
                    onClear();
                    window.location.href = `/payment/success?session_id=${orderId}`;
                },
            });
        } catch {
            toast.error(t("cart.errorFail"));
            setRzpPaying(false);
        }
    };

    const orderByWhatsApp = () => {
        if (!formValid()) return;
        const lines = cart.map((i) => `${i.qty} x ${i.name} - Rs ${i.price * i.qty}`).join("\n");
        const msg = [
            "Hello Al Zahra! I would like to order:",
            "",
            lines,
            "",
            fee ? `Delivery (${quote.courier}): Rs ${fee}` : null,
            `Total: Rs ${grand}`,
            "",
            `Name: ${form.name}`,
            `Phone: ${form.phone}`,
            `Address: ${form.address}${form.pincode ? ` - ${form.pincode}` : ""}${form.city ? `, ${form.city}, ${form.state}` : ""}`,
        ].filter((l) => l !== null).join("\n");
        window.open(`https://wa.me/916291341914?text=${encodeURIComponent(msg)}`, "_blank");
    };

    const submit = async () => {
        if (!formValid()) return;
        setPlacing(true);
        try {
            await axios.post(`${API}/orders`, {
                ...payload(),
                items: cart.map((i) => ({ product_id: i.id, qty: i.qty })),
            });
            setPlaced(true);
            onClear();
        } catch {
            toast.error(t("cart.errorFail"));
        } finally {
            setPlacing(false);
        }
    };

    const close = () => {
        onClose();
        setTimeout(() => setPlaced(false), 400);
    };

    return (
        <Sheet open={open} onOpenChange={(v) => !v && close()}>
            <SheetContent side="right" className="w-full sm:max-w-md bg-ivory border-l border-gold/30 p-0 flex flex-col" data-testid="cart-drawer">
                <SheetHeader className="px-8 pt-8 pb-6 border-b border-gold/20">
                    <SheetTitle className="font-serif text-3xl tracking-tight text-charcoal font-normal">{t("cart.title")}</SheetTitle>
                </SheetHeader>

                {placed ? (
                    <div className="flex-1 flex flex-col items-center justify-center px-10 text-center" data-testid="order-success-message">
                        <span className="w-14 h-14 border border-gold flex items-center justify-center mb-8">
                            <Check size={22} className="text-golddark" />
                        </span>
                        <h3 className="font-serif text-3xl text-charcoal mb-4">{t("cart.successTitle")}</h3>
                        <p className="text-sm text-smoke leading-relaxed">
                            {t("cart.successBody")(form.name.split(" ")[0], form.phone)}
                        </p>
                        <button
                            data-testid="order-continue-button"
                            onClick={close}
                            className="mt-10 border border-gold text-golddark px-10 py-4 text-xs tracking-[0.25em] uppercase hover:bg-gold hover:text-ivory transition-colors duration-300"
                        >
                            {t("cart.continue")}
                        </button>
                    </div>
                ) : cart.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center px-10 text-center" data-testid="cart-empty-state">
                        <p className="font-serif text-2xl text-ash italic">{t("cart.empty1")}</p>
                        <p className="text-xs text-ash mt-3 tracking-wide">{t("cart.empty2")}</p>
                    </div>
                ) : (
                    <>
                        <div data-lenis-prevent className="flex-1 overflow-y-auto overscroll-contain px-8 py-6 space-y-8">
                            <div ref={deliveryRef} className="space-y-5 border border-gold/30 bg-cream/50 p-6" data-testid="delivery-details-panel">
                                <p className="text-xs tracking-[0.3em] uppercase text-golddark">{t("cart.delivery")}</p>
                                <Field label={t("cart.name")} data-testid="checkout-name-input" value={form.name} onChange={set("name")} placeholder={t("cart.phName")} />
                                <Field label={t("cart.phone")} data-testid="checkout-phone-input" value={form.phone} onChange={set("phone")} placeholder={t("cart.phPhone")} />
                                <Field label={t("cart.address")} data-testid="checkout-address-input" value={form.address} onChange={set("address")} placeholder={t("cart.phAddress")} />
                                <Field label={lp.email} data-testid="checkout-email-input" type="email" value={form.email} onChange={set("email")} placeholder="name@example.com" />
                                <div>
                                    <Field label={lp.pin} data-testid="checkout-pincode-input" inputMode="numeric" value={form.pincode} onChange={setPin} placeholder={lp.phPin} />
                                    {form.city && (
                                        <p className="text-[11px] text-golddark mt-1.5 tracking-wide" data-testid="checkout-pincode-city">{form.city}, {form.state}</p>
                                    )}
                                </div>
                            </div>

                            {cart.map((item) => (
                                <div key={item.id} className="flex gap-4 border-b border-gold/15 pb-6" data-testid={`cart-item-${item.id}`}>
                                    <img src={item.image} alt={item.name} className="w-16 h-20 object-cover border border-gold/20" />
                                    <div className="flex-1">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p className="font-serif text-lg text-charcoal">{item.name}</p>
                                                <p className="text-[10px] tracking-[0.2em] uppercase text-ash mt-1">{item.tag}</p>
                                            </div>
                                            <button data-testid={`cart-remove-${item.id}`} onClick={() => onRemove(item.id)} className="text-ash hover:text-charcoal transition-colors">
                                                <X size={15} />
                                            </button>
                                        </div>
                                        <div className="flex justify-between items-center mt-4">
                                            <div className="flex items-center border border-gold/30">
                                                <button data-testid={`cart-qty-minus-${item.id}`} onClick={() => onQty(item.id, -1)} className="px-3 py-1.5 text-smoke hover:text-golddark transition-colors">
                                                    <Minus size={12} />
                                                </button>
                                                <span className="px-3 text-sm text-charcoal" data-testid={`cart-qty-${item.id}`}>{item.qty}</span>
                                                <button data-testid={`cart-qty-plus-${item.id}`} onClick={() => onQty(item.id, 1)} className="px-3 py-1.5 text-smoke hover:text-golddark transition-colors">
                                                    <Plus size={12} />
                                                </button>
                                            </div>
                                            <span className="font-serif text-lg text-charcoal">{formatPrice(item.price * item.qty, lang, item.aed != null ? item.aed * item.qty : null)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="border-t border-gold/20 px-8 py-6">
                            <p className="text-[10px] tracking-[0.3em] uppercase text-golddark mb-3">{lp.method}</p>
                            <div className="grid grid-cols-3 gap-2 mb-6" data-testid="payment-method-options">
                                {[
                                    razorpay.enabled && ["razorpay", Smartphone, lp.upi, lp.upiSub],
                                    ["stripe", CreditCard, lp.card, lp.cardSub],
                                    ["cod", Banknote, lp.cod, lp.codSub],
                                ].filter(Boolean).map(([key, Icon, label, sub]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        data-testid={`payment-method-${key}`}
                                        aria-pressed={activeMethod === key}
                                        onClick={() => setMethod(key)}
                                        className={`text-left p-3 border transition-colors duration-300 ${
                                            activeMethod === key ? "border-charcoal bg-charcoal text-goldlight" : "border-gold/30 text-charcoal hover:border-gold"
                                        }`}
                                    >
                                        <Icon size={15} strokeWidth={1.5} className={activeMethod === key ? "text-gold" : "text-golddark"} />
                                        <p className="text-[11px] tracking-wide mt-2 leading-tight">{label}</p>
                                        <p className={`text-[9px] mt-1 leading-tight ${activeMethod === key ? "text-goldlight/70" : "text-ash"}`}>{sub}</p>
                                    </button>
                                ))}
                            </div>

                            <div className="space-y-2 mb-5 text-sm">
                                <div className="flex justify-between text-smoke">
                                    <span className="text-xs tracking-[0.2em] uppercase">{lp.subtotal}</span>
                                    <span data-testid="cart-subtotal">{cartTotal(cart, lang)}</span>
                                </div>
                                <div className="flex justify-between text-smoke" data-testid="cart-delivery">
                                    <span className="text-xs tracking-[0.2em] uppercase">
                                        {lp.delivery}{quote?.available && quote.courier ? <span className="normal-case tracking-normal text-ash"> · {lp.deliveryVia} {quote.courier}</span> : null}
                                    </span>
                                    <span data-testid="cart-delivery-fee">
                                        {quoting ? lp.quoting : quote?.available ? (fee ? `₹${fee}` : lp.free) : form.pincode.length === 6 ? lp.deliveryFree : pinRequired ? lp.deliveryPin : lp.deliveryFree}
                                    </span>
                                </div>
                                <div className="flex justify-between items-baseline pt-3 border-t border-gold/20">
                                    <span className="text-xs tracking-[0.25em] uppercase text-smoke">{t("cart.total")}</span>
                                    <span className="font-serif text-2xl text-charcoal" data-testid="cart-total">{fee ? `₹${grand}` : cartTotal(cart, lang)}</span>
                                </div>
                            </div>

                            <button
                                data-testid="checkout-submit-button"
                                onClick={activeMethod === "razorpay" ? payRazorpay : activeMethod === "stripe" ? payOnline : submit}
                                disabled={paying || rzpPaying || placing || quoting}
                                className="w-full flex items-center justify-center gap-2 bg-charcoal text-goldlight px-10 py-5 text-xs tracking-[0.25em] uppercase hover:bg-golddark hover:text-ivory transition-colors duration-300 disabled:opacity-50"
                            >
                                {activeMethod === "cod" ? <Banknote size={13} strokeWidth={1.5} /> : <Lock size={13} strokeWidth={1.5} />}
                                {paying || rzpPaying || placing
                                    ? t("cart.placing")
                                    : activeMethod === "cod" ? `${lp.placeCod} — ₹${grand}` : `${lp.pay} ₹${grand}`}
                            </button>
                            <button
                                data-testid="phone-order-button"
                                onClick={orderByWhatsApp}
                                className="w-full mt-3 flex items-center justify-center gap-2 border border-charcoal/60 text-charcoal px-10 py-4 text-xs tracking-[0.25em] uppercase hover:bg-charcoal hover:text-goldlight transition-colors duration-300"
                            >
                                <MessageCircle size={13} strokeWidth={1.5} />
                                {lp.phoneOrder}
                            </button>
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
};
